import { computed, reactive, unref } from 'vue';

// Module level state management via this reactive variable - allows us to track and use
// provider profile progress across the app via composable vs. store.
const progressByProviderId = reactive({});

function getOrCreateRecord(providerId) {
  if (!providerId) return null;

  if (!progressByProviderId[providerId]) {
    progressByProviderId[providerId] = reactive({});
  }
  return progressByProviderId[providerId];
}

/**
 * Shared per-provider section completion (0–100) for manager provider profile editing.
 * State lives on the module so ProviderShow, ManagerMainNav, and any other caller stay in sync.
 *
 * @param {import('vue').Ref<string | undefined> | import('vue').ComputedRef<string | undefined> | (() => string | undefined)} providerIdRef
 */
export function useProviderProfileSectionProgress(providerIdRef) {
  const progress = computed(() => {
    const providerId = unref(providerIdRef);
    return getOrCreateRecord(providerId) ?? {};
  });

  function seedSectionKeys(sectionKeys) {
    const providerId = unref(providerIdRef);
    if (!providerId || !sectionKeys?.length) return;

    const record = getOrCreateRecord(providerId);
    const allowed = new Set(sectionKeys);
    Object.keys(record).forEach((key) => {
      if (!allowed.has(key)) delete record[key];
    });

    sectionKeys.forEach((key) => {
      if (record[key] === undefined) {
        record[key] = 0;
      }
    });
  }

  function setSectionProgress(sectionKey, value) {
    const providerId = unref(providerIdRef);
    if (!providerId) return;

    const record = getOrCreateRecord(providerId);
    record[sectionKey] = value;
  }

  function clearProviderProgress(targetProviderId) {
    const providerId = targetProviderId ?? unref(providerIdRef);
    if (!providerId) return;

    delete progressByProviderId[providerId];
  }

  const totalProgress = computed(() => {
    const providerId = unref(providerIdRef);
    const record = providerId ? progressByProviderId[providerId] : null;
    if (!record) return 0;

    const keys = Object.keys(record);
    if (keys.length === 0) return 0;

    const sum = Object.values(record).reduce((memo, value) => memo + Number(value), 0);
    return parseInt(sum / keys.length, 10);
  });

  function progressPercentForSection(sectionKey) {
    const providerId = unref(providerIdRef);
    if (!providerId) return 0;

    const record = progressByProviderId[providerId];
    if (!record) return 0;

    return Number(record[sectionKey] ?? 0);
  }

  return {
    clearProviderProgress,
    progress,
    progressPercentForSection,
    seedSectionKeys,
    setSectionProgress,
    totalProgress,
  };
}
